'use client';

import { ControlledOtpInput } from '@/components/auth/controlled-otp-input';
import { FormSubmitButton } from '@/components/auth/form-submit-button';
import { ResendControl } from '@/components/auth/resend-control';
import { useLoadingCallback } from '@/lib/hooks/use-loading-callback';
import { otpSchema } from '@/schemas/auth';
import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

type FormData = z.infer<typeof otpSchema>;

interface OtpVerifyFormProps {
    email: string;
    onVerify: (otp: string) => Promise<void>;
    onResend: () => Promise<unknown>;
    submitButtonText: string;
    showLoginOptions?: boolean;
    // These are only relevant for login flow
    onSwitchToPassword?: () => void;
    onUseDifferentEmail?: () => void;
}

export function OtpVerifyForm({
    email,
    onVerify,
    onResend,
    submitButtonText,
    showLoginOptions = false,
    onSwitchToPassword,
    onUseDifferentEmail,
}: OtpVerifyFormProps) {
    const otpVerifyForm = useForm<FormData>({
        resolver: zodResolver(otpSchema),
        mode: 'onChange', // Real-time validation
        defaultValues: {
            code: '',
        },
    });

    const [handleVerifyOtp, verifyingOtp] = useLoadingCallback(
        async (data: FormData) => {
            await onVerify(data.code);
        }
    );

    const handleResendOtp = async () => {
        await onResend();
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="text-center">
                <p className="mb-4 text-sm text-zinc-400">
                    We sent a 6-digit code to <strong>{email}</strong>. The code
                    will expire in 10 minutes.
                </p>
            </div>
            <Form
                className="flex flex-col items-center gap-2"
                onSubmit={otpVerifyForm.handleSubmit(handleVerifyOtp)}
            >
                <div className="justify-center text-center">
                    <ControlledOtpInput
                        control={otpVerifyForm.control}
                        length={6}
                        name="code"
                    />
                </div>
                <div className="flex w-full flex-row gap-2">
                    <FormSubmitButton
                        className="flex-1"
                        color="primary"
                        control={otpVerifyForm.control}
                        isLoading={verifyingOtp}
                        radius="sm"
                    >
                        {submitButtonText}
                    </FormSubmitButton>
                    <ResendControl onResend={handleResendOtp}>
                        Resend code
                    </ResendControl>
                </div>
                {showLoginOptions && (
                    <>
                        <Button
                            fullWidth
                            onPress={onUseDifferentEmail}
                            variant="bordered"
                        >
                            Use a different email
                        </Button>
                        <Button
                            fullWidth
                            onPress={onSwitchToPassword}
                            variant="bordered"
                        >
                            Sign in with password
                        </Button>
                    </>
                )}
            </Form>
        </div>
    );
}
